import { Avatar, type AvatarProps, Badge, useTheme } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';

/**
 * Deterministic color palette for individual customer / staff avatars.
 * Each user gets a consistent color based on their name.
 */
const AVATAR_COLORS = [
  '#1976d2', // blue
  '#388e3c', // green
  '#d32f2f', // red
  '#7b1fa2', // purple
  '#f57c00', // orange
  '#0097a7', // teal
  '#c2185b', // pink
  '#5d4037', // brown
  '#455a64', // blue-grey
  '#512da8', // deep purple
  '#00796b', // dark teal
  '#e64a19', // deep orange
  '#303f9f', // indigo
  '#689f38', // light green
  '#ffa000', // amber
  '#0288d1', // light blue
];

/**
 * Business customer avatars use a transparent background so they blend
 * into whatever surface they sit on (dark or light mode). Only the
 * initials are visible, giving a clean "floating text" look that
 * clearly distinguishes them from individual customer avatars.
 */

function getColorFromName(firstName: string, lastName: string): string {
  const str = `${firstName}${lastName}`.toLowerCase();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? '#757575';
}

function getInitials(firstName: string, lastName: string): string {
  const f = firstName.charAt(0);
  const l = lastName.charAt(0);
  return (l ? `${f}${l}` : f).toUpperCase();
}

function splitName(name: string): [string, string] {
  const parts = name.trim().split(/\s+/);
  return [parts[0] ?? '', parts.slice(1).join(' ') || ''];
}

type UserAvatarProps = Omit<AvatarProps, 'children' | 'variant'> & {
  avatarUrl?: string | null;
  size?: number;
  /** 'circular' (default) for staff, 'rounded' for customers */
  variant?: 'circular' | 'rounded';
  /** 'default' for individuals/staff, 'business' for business customers (transparent bg) */
  colorScheme?: 'default' | 'business';
  /** Show a small business icon badge in the bottom-right corner */
  showBusinessBadge?: boolean;
} & (
  | { firstName: string; lastName: string; name?: never }
  | { name: string; firstName?: never; lastName?: never }
);

export function UserAvatar({ avatarUrl, size, variant = 'circular', colorScheme = 'default', showBusinessBadge = false, sx, ...rest }: UserAvatarProps) {
  const theme = useTheme();
  const first = 'name' in rest && rest.name != null ? splitName(rest.name)[0] : (rest as { firstName: string }).firstName;
  const last = 'name' in rest && rest.name != null ? splitName(rest.name)[1] : (rest as { lastName: string }).lastName;

  // Remove custom props before spreading to Avatar
  const { name: _n, firstName: _f, lastName: _l, ...avatarProps } = rest as Record<string, unknown>;

  const isBusiness = colorScheme === 'business';
  const bgcolor = isBusiness ? 'transparent' : getColorFromName(first, last);
  const initials = getInitials(first, last);
  const fontSize = size ? size * 0.35 : '0.85rem';

  const avatar = (
    <Avatar
      src={avatarUrl || undefined}
      variant={variant === 'rounded' ? 'rounded' : 'circular'}
      sx={{
        bgcolor,
        color: isBusiness ? 'text.primary' : '#fff',
        fontWeight: isBusiness ? 700 : undefined,
        fontSize,
        ...(size ? { width: size, height: size } : {}),
        ...(variant === 'rounded' ? { borderRadius: '20%' } : {}),
        ...sx,
      }}
      {...avatarProps}
    >
      {initials}
    </Avatar>
  );

  if (!showBusinessBadge) {
    return avatar;
  }

  return (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        <BusinessIcon
          sx={{
            fontSize: 12,
            color: theme.palette.text.secondary,
            bgcolor: theme.palette.background.paper,
            borderRadius: '50%',
            padding: '2px',
            boxShadow: `0 0 0 1px ${theme.palette.divider}`,
          }}
        />
      }
    >
      {avatar}
    </Badge>
  );
}
